import React, { useId, useRef, useState } from 'react';
import Icon from './Icon';
import SelectMenu from './SelectMenu';
import { menuOptions, typeAhead } from './selectOptions';
import './Select.css';

const MOVES = { ArrowDown: 1, ArrowUp: -1, PageDown: 6, PageUp: -6 };

/**
 * The ResUI Select (PRD-1702), ported from the Navy handoff's selects.js and
 * built to ResUI Design Decisions, Select. It replaces the browser's select on
 * Navy screens.
 *
 * - The field is the Input with a 12 Bold caret: a button with role combobox,
 *   named by `ariaLabel` or `labelledBy`, ringed while it has focus or its menu
 *   is open (Active). It is as wide as its longest option.
 * - The menu is Context Menu options in a listbox, fixed to the window, six
 *   showing. It opens on the chosen option, washed and Medium with a check;
 *   the ring on the active option shows only from the keyboard.
 * - Keys: on the field, Enter, Space, Up or Down open it, and a letter opens it
 *   on the first match. In the menu, Up and Down move, Page Up and Page Down
 *   by six, Home and End to the ends, typing jumps, Enter or Space choose,
 *   Escape closes without changing and hands focus back, Tab closes and moves on.
 * - Moving the page closes it; scrolling the list doesn't.
 *
 * Named by `ariaLabel`, by `labelledBy`, or by a `<label htmlFor={id}>`
 * (a button is labelable); `describedBy` for a hint.
 *
 * options: [{ value, label }] or labels. `scale` keeps their order (sort
 * orders, date ranges); otherwise they're alphabetical (see menuOptions).
 */
function Select({ id, value, options, onChange, ariaLabel, labelledBy, describedBy, disabled = false, scale = false, className = '' }) {
  const listId = `${useId()}-list`;
  const { list, placeholder } = menuOptions(options, { scale });
  const chosen = list.findIndex((o) => o.value === value);
  const showPlaceholder = !!placeholder && (chosen < 0 || value === '');
  const fieldRef = useRef(null);
  const listRef = useRef(null);
  const typing = useRef({});
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [byKeyboard, setByKeyboard] = useState(false);

  const openMenu = (keyboard, at) => {
    setByKeyboard(keyboard);
    setActive(at ?? Math.max(0, chosen));
    setOpen(true);
  };
  const close = (focusField) => {
    setOpen(false);
    if (focusField) fieldRef.current?.focus({ preventScroll: true });
  };
  const choose = (k) => {
    close(true);
    if (list[k] && list[k].value !== value) onChange(list[k].value);
  };

  const moveTo = (k) => {
    const i = Math.max(0, Math.min(list.length - 1, k));
    setActive(i);
    const lst = listRef.current;
    const el = lst?.children[i];
    if (!el) return;
    if (el.offsetTop - 4 < lst.scrollTop) lst.scrollTop = el.offsetTop - 4;
    else if (el.offsetTop + el.offsetHeight + 4 > lst.scrollTop + lst.clientHeight) lst.scrollTop = el.offsetTop + el.offsetHeight + 4 - lst.clientHeight;
  };

  const onFieldKeyDown = (e) => {
    if (open || disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); openMenu(true); return; }
    if (e.key.length === 1 && e.key !== ' ' && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const k = typeAhead(typing.current, e.key, chosen, list.map((o) => o.label));
      if (k > -1) { e.preventDefault(); openMenu(true, k); }
    }
  };

  const onListKeyDown = (e) => {
    setByKeyboard(true);
    if (e.key in MOVES) { e.preventDefault(); moveTo(active + MOVES[e.key]); }
    else if (e.key === 'Home' || e.key === 'End') { e.preventDefault(); moveTo(e.key === 'Home' ? 0 : list.length - 1); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(active); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(true); }
    // Tab leaves from the field, as it would without the menu: focus goes back
    // to it first, so the browser's Tab moves on from there, inside a dialog too
    // (the menu itself sits at the end of the page).
    else if (e.key === 'Tab') close(true);
    else if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const k = typeAhead(typing.current, e.key, active, list.map((o) => o.label));
      if (k > -1) { e.preventDefault(); moveTo(k); }
    }
  };

  const shown = showPlaceholder ? placeholder : list[chosen]?.label ?? '';
  return (
    <>
      <button
        ref={fieldRef}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        disabled={disabled}
        className={`resui-select-field${className ? ` ${className}` : ''}`}
        data-component="Input"
        // While open, focus stays in the list: where a click doesn't focus a button
        // (Safari, Firefox on Mac) the list would blur and close, and the click reopen it.
        onMouseDown={(e) => { if (open) e.preventDefault(); }}
        onClick={(e) => (open ? close(true) : openMenu(e.detail === 0))}
        onKeyDown={onFieldKeyDown}
      >
        <span className="resui-select-text">
          <span className={`resui-select-value${showPlaceholder ? ' is-placeholder' : ''}`}>{shown}</span>
          {/* As wide as its longest option, so the field doesn't change width with the choice. */}
          {[placeholder, ...list.map((o) => o.label)].filter(Boolean).map((label, k) => (
            // eslint-disable-next-line react/no-array-index-key
            <span key={k} className="resui-select-sizer" aria-hidden="true">{label}</span>
          ))}
        </span>
        <Icon name="caret-down" size={12} className="resui-select-caret" />
      </button>
      {open && (
        <SelectMenu
          fieldRef={fieldRef}
          listRef={listRef}
          listId={listId}
          list={list}
          chosen={showPlaceholder ? -1 : chosen}
          active={active}
          byKeyboard={byKeyboard}
          ariaLabel={ariaLabel}
          labelledBy={labelledBy}
          onKeyDown={onListKeyDown}
          onBlur={() => close(false)}
          onChoose={choose}
          onClose={close}
        />
      )}
    </>
  );
}

export default Select;
